import React from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Box, IconButton, Paper, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import RemoveIcon from "@mui/icons-material/Remove";
import Typography from "components/basic/typography";

import ConversationThread from "../../../ConversationThread";
import { ConversationView } from "../../../../types";

interface ChatPopupProps {
  conversation: ConversationView;
  minimized: boolean;
  onToggleMinimize: () => void;
  onClose: () => void;
  onOpenFull: () => void;
}

// One floating chat box. Minimized it collapses to just the header bar;
// expanded it holds a live ConversationThread (feed + composer).
const ChatPopup: React.FC<ChatPopupProps> = ({
  conversation,
  minimized,
  onToggleMinimize,
  onClose,
  onOpenFull,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("messages");

  return (
    <Paper
      elevation={6}
      sx={{
        width: 320,
        maxWidth: "calc(100vw - 32px)",
        height: minimized ? "auto" : 420,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={theme.spacing(1)}
        px={1.5}
        py={1}
        borderBottom={minimized ? "none" : `1px solid ${theme.palette.divider}`}
        onClick={minimized ? onToggleMinimize : undefined}
        sx={{ cursor: minimized ? "pointer" : "default" }}
      >
        <Badge
          badgeContent={conversation.unreadCount}
          color="error"
          invisible={!minimized || conversation.unreadCount === 0}
        >
          <Avatar
            src={conversation.otherPartyAvatarUrl ?? undefined}
            imgProps={{ referrerPolicy: "no-referrer" }}
            sx={{ width: 32, height: 32 }}
          />
        </Badge>
        <Box
          flexGrow={1}
          minWidth={0}
          title={t("popup.open_in_messages")}
          sx={{ cursor: "pointer" }}
          onClick={(event) => {
            event.stopPropagation();
            onOpenFull();
          }}
        >
          <Typography variant="body1" noWrap style={{ fontWeight: "bold" }}>
            {conversation.otherPartyName}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={t(minimized ? "popup.expand" : "popup.minimize")}
          onClick={(event) => {
            event.stopPropagation();
            onToggleMinimize();
          }}
        >
          {minimized ? (
            <OpenInFullIcon fontSize="small" />
          ) : (
            <RemoveIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          aria-label={t("popup.close")}
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {!minimized && (
        <ConversationThread key={conversation.id} conversation={conversation} />
      )}
    </Paper>
  );
};

export default ChatPopup;
